// Publish (the Mac's PublishPage, PublishRoom.swift): the formats down the side (the built-ins with their versions, then
// More formats and Your formats), a preview of the chosen format in the middle made *before* building (a sample of the
// opening chapters, typeset by Typst for PDF and Word presets, the ebook's sample page, plain text as written), and on
// the right what it'll be built with, the cheap checks and one build button. After building, preflight replaces the
// checks, with Save, Print (PDFs), Add Copyfitting Notes (print) and Build Again; every build records its checkpoint.
// Compiling runs in the compile worker and Typst in its own; both start here and end when the writer leaves.
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { journalPostsInTree, type OpenedProject, type TreeNode } from '@writing-suite/core';
import {
  adjustedID, builtInOfAdjusted, FormatAdjustment, isAdjustable, isAdjustedPreset, PresetLibrary, projectPresetSources, settingPaths, typedMarket,
  type FormatSetting, type LastPrint, type MarketRules, type PreflightFinding, type PublishFormat, type ReadinessItem,
} from '@writing-suite/compile';
import { planEdit } from '../store/plan.ts';
import type { PageProps } from '../shell/ProjectShell.tsx';
import { announce, useApp, usePreference } from '../shell/context.ts';
import { WorkspaceContext } from '../shell/workspace.ts';
import { authorName } from '../store/useProject.ts';
import { PublishSession, readLastPrint, recordLastPrint } from '../store/publish.ts';
import { connectCompileWorker, typstFactory } from '../publish/client.ts';
import type { BuildResult, FormatList, Preparation, Preview, Progress, Selection } from '../publish/protocol.ts';
import { clock } from '../publish/summary.ts';
import { renderSample } from '../publish/markup.ts';
import { LaidOutPages } from './LaidOutPages.tsx';
import { BylineEditor } from './Byline.tsx';
import { FormatSettings } from './FormatSettings.tsx';
import { saveFile } from '../host/host.ts';

/** Previews by project and preset, kept while the tab is open (the Mac's `publishPreviewCache`): shown again at once
 *  when the writer comes back, and remade only when the sample has changed. */
const previewCache = new Map<string, { key: string; preview: Preview }>();

const errorText = (e: unknown) => (e instanceof Error ? e.message : String(e));

/** What answering a readiness item by its setting is called (PublishRoom.swift's `fixTitle`). */
const FIX_TITLES: Partial<Record<FormatSetting, string>> = { keepHighlights: 'Keep Them' };
const plural = (n: number, noun: string) => `${n.toLocaleString('en-GB')} ${noun}${n === 1 ? '' : 's'}`;

/** A journal's posts (`journalPosts`: not folders, nor the sections under a post), in the manuscript's order. */
function postsOf(project: OpenedProject): { id: string; label: string }[] {
  const id = project.manuscriptId();
  if (!id || project.type !== 'journal') return [];
  const tree = project.tree(id);
  const titles = new Map<string, string>();
  const walk = (nodes: TreeNode[]) => { for (const n of nodes) { if (n.fragmentId) titles.set(n.fragmentId, n.title); walk(n.children); } };
  walk(tree);
  return journalPostsInTree(tree).map((p) => ({ id: p.fragmentId, label: titles.get(p.fragmentId) || 'Untitled post' }));
}

function download(name: string, bytes: Uint8Array, mime: string) {
  void saveFile(name, bytes, mime);
}

export function PublishPage({ project, live, tree }: PageProps) {
  const app = useApp();
  const workspace = useContext(WorkspaceContext);
  const opened = project as OpenedProject;
  const projectKey = opened.id;
  const [session, setSession] = useState<PublishSession | null>(null);
  const [formats, setFormats] = useState<FormatList | null>(null);
  const [formatsError, setFormatsError] = useState<string | null>(null);
  const [formatId, setFormatId] = usePreference<string>(`publishFormat-${projectKey}`, '');
  const [variants, setVariants] = usePreference<Record<string, string>>(`publishVariants-${projectKey}`, {});
  const posts = useMemo(() => postsOf(opened), [opened]);
  const [postId, setPostId] = useState<string | null>(null);
  const [preparation, setPreparation] = useState<Preparation | null>(null);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [building, setBuilding] = useState(false);
  const [progress, setProgress] = useState<Progress | null>(null);
  const [built, setBuilt] = useState<BuildResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copyfitMessage, setCopyfitMessage] = useState<string | null>(null);
  const [page, setPage] = useState<number | null>(null);
  const [limit, setLimit] = useState('');
  const [showPublication, setShowPublication] = useState(false);
  const [copied, setCopied] = useState(false);
  // Build only what the contents' filter lets through (offered while the contents are filtered).
  const [onlyFiltered, setOnlyFiltered] = useState(false);
  // Adjust…: the format's settings in place of the details.
  const [adjusting, setAdjusting] = useState(false);

  // The project's own presets (Your formats), as they stand: the list is read again when they change.
  const presetsKey = JSON.stringify(opened.record?.settings.compilePresets ?? null);

  // The workers live while Publish is open.
  useEffect(() => {
    if (!live) return;
    const s = new PublishSession({ live, connect: connectCompileWorker, typst: typstFactory(), device: app.account.deviceId ?? 'web' });
    setSession(s);
    return () => {
      s.dispose();
      setSession(null);
    };
  }, [live, app.account.deviceId]);

  useEffect(() => {
    if (!session) return;
    let current = true;
    session.formats().then((f) => current && (setFormats(f), setFormatsError(null)), (e) => current && setFormatsError(errorText(e)));
    return () => {
      current = false;
    };
  }, [session, presetsKey]);

  const all = useMemo(() => (formats ? [...formats.builtIn, ...formats.more, ...formats.yours] : []), [formats]);
  const format: PublishFormat | null = all.find((f) => f.id === formatId) ?? formats?.builtIn[0] ?? all[0] ?? null;
  // The chosen version, adjusted or not since it was chosen.
  const chosenVariant = (f: PublishFormat) => {
    const chosen = variants[f.id];
    if (!chosen) return null;
    return [chosen, adjustedID(chosen), builtInOfAdjusted(chosen)].find((id) => id && f.variants.some((v) => v.presetID === id)) ?? null;
  };
  const presetId = format ? (chosenVariant(format) ?? format.variants[0]?.presetID) : null;
  const ownPresets = useMemo(() => projectPresetSources(opened.record?.settings ?? {}), [presetsKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const adjustable = useMemo(() => {
    if (!presetId) return false;
    try { return isAdjustable(new PresetLibrary(ownPresets).preset(presetId)); } catch { return false; }
  }, [presetId, ownPresets]);
  const firstChapter = useMemo(() => {
    const folder = tree.find((n) => n.isFolder && n.title) ?? null;
    return { number: 1, title: folder?.title ?? 'The Weir' };
  }, [tree]);
  const filtered = format && format.kind !== 'submission' && format.kind !== 'post' ? (workspace?.filter?.filteredManuscript ?? null) : null;
  const post = format?.kind === 'post' ? (postId && posts.some((p) => p.id === postId) ? postId : (posts[0]?.id ?? null)) : null;
  const writer = useMemo(() => ({ name: authorName() ?? '' }), []);
  const selection: Selection | null = format && presetId ? { formatKind: format.kind, presetId, writer, post } : null;
  const selectionKey = selection ? `${selection.formatKind}|${selection.presetId}|${selection.post ?? ''}` : '';
  const lastPrint: LastPrint | null = useMemo(() => readLastPrint(projectKey), [projectKey, built]);
  const summary = preparation?.summary ?? null;
  const pdfMissing = !!session && !session.hasPDF && (summary?.format === 'pdf' || (format?.kind === 'screenplay' && presetId !== 'screenplay-fdx' && presetId !== 'screenplay-fountain'));
  const recordsAudio = presetId === 'audiobook';

  const reset = useCallback(() => {
    setBuilt(null);
    setError(null);
    setCopyfitMessage(null);
    setPage(null);
    setCopied(false);
  }, []);
  useEffect(reset, [selectionKey, reset]);

  // Before it's built: prepared and previewed whenever the choice or the project changes (a little after, so a burst
  // of changes from another device is one preparation).
  const generation = useRef(0);
  useEffect(() => {
    if (!session || !selection) return;
    const mine = ++generation.current;
    const cacheKey = `${projectKey}|${selectionKey}`;
    const cached = previewCache.get(cacheKey);
    setPreview(cached?.preview ?? null);
    const timer = setTimeout(() => {
      void session.prepare(selection, lastPrint).then((p) => {
        if (generation.current === mine) setPreparation(p);
      }, (e) => generation.current === mine && setPreparation({ summary: null, readiness: [], documents: [], settings: [], error: errorText(e) }));
      void session.preview(selection, cached?.key ?? null).then((r) => {
        if (generation.current !== mine) return;
        if (r.preview) {
          if (r.key) previewCache.set(cacheKey, { key: r.key, preview: r.preview });
          setPreview(r.preview);
        } else if (cached) {
          setPreview(cached.preview);
        }
      }, (e) => generation.current === mine && setPreview({ kind: 'message', message: `Couldn’t make a preview: ${errorText(e)}` }));
    }, 60);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, selectionKey, project, lastPrint]);
  useEffect(() => setPreparation(null), [selectionKey]);

  const market: MarketRules | null = useMemo(() => {
    const n = Number(limit.replace(/[^\d]/g, ''));
    return n > 0 ? typedMarket('the market', { maxWords: n }) : null;
  }, [limit]);

  // Compiling under the project's byline is the owner's (W9a): a co-writer or commenter previews, never builds.
  const owner = (workspace?.role ?? 'owner') === 'owner';

  // A readiness item's one action ("Keep Them"): the switch turned on in the format's own version, as Adjust… saves it
  // (`LibraryModel.adjustFormat(_:turningOn:)`); the page prepares again as the presets change.
  const turnOn = (setting: FormatSetting) => {
    if (!live?.writable || !presetId) return;
    try {
      const library = new PresetLibrary(ownPresets);
      const a = ownPresets[presetId] !== undefined
        ? FormatAdjustment.make(presetId, ownPresets[presetId], library.parent(presetId) ?? 'base', library)
        : FormatAdjustment.make(adjustedID(presetId), ownPresets[adjustedID(presetId)] ?? null, presetId, library);
      const next = a.set(true, settingPaths(setting)[0]);
      planEdit(live, (e) => e.setCompilePreset(next.id, next.yaml));
    } catch (e) {
      setError(errorText(e));
    }
  };
  const canBuild = owner && !!selection && !building && !!live?.writable && !pdfMissing && !recordsAudio
    // A pack needs one written document; a play's always has its characters and sample, made from the play.
    && (format?.kind === 'submission' ? (preparation?.documents.some((d) => d.words > 0 || d.kind === 'cast-list') ?? false) : !!summary);

  const selectionKeyRef = useRef(selectionKey);
  selectionKeyRef.current = selectionKey;
  const build = async () => {
    if (!session || !selection || !format) return;
    reset();
    setBuilding(true);
    const shown = selectionKey;
    try {
      const result = format.kind === 'submission'
        ? await session.pack(format.variants.map((v) => v.presetID), writer)
        : await session.build(selection, { market, progress: setProgress, limit: onlyFiltered && filtered ? filtered.fragments : null });
      if (result.lastPrint) recordLastPrint(projectKey, result.lastPrint);
      if (shown !== selectionKeyRef.current) return;
      setBuilt(result);
      const problems = result.findings.filter((f) => f.severity === 'error').length;
      announce(`Built ${result.zip?.name ?? result.files[0]?.name ?? ''}. ${problems ? plural(problems, 'problem') : 'Preflight passed'}.`);
    } catch (e) {
      if (shown === selectionKeyRef.current) setError(errorText(e));
    } finally {
      setBuilding(false);
      setProgress(null);
    }
  };
  const save = () => {
    if (!built) return;
    if (built.zip) return download(built.zip.name, built.zip.bytes, 'application/zip');
    const f = built.files[0];
    if (f) download(f.name, f.bytes, f.mime);
  };
  const builtPDF = built && !built.zip ? built.files.find((f) => f.mime === 'application/pdf') ?? null : null;
  const openPDF = (bytes: Uint8Array) => {
    const url = URL.createObjectURL(new Blob([bytes as BlobPart], { type: 'application/pdf' }));
    window.open(url, '_blank', 'noopener');
    setTimeout(() => URL.revokeObjectURL(url), 120_000);
  };
  const copyfit = built ? built.findings.filter((f) => f.copyfit && f.quote) : [];
  const addCopyfit = () => {
    if (!session || !built) return;
    try {
      const r = session.addCopyfitNotes(built.findings, built.reports[0]?.checkpoint?.id ?? null);
      const text = `Added to the margin: ${plural(r.placed, 'copyfitting note')}.` + (r.unplaced ? ` ${r.unplaced} couldn’t be matched to the text and are in the Inbox.` : '');
      setCopyfitMessage(text);
      announce(text);
    } catch (e) {
      setCopyfitMessage(`Couldn’t add the notes: ${errorText(e)}`);
    }
  };
  const copyPost = async () => {
    const f = built?.files[0];
    if (!f) return;
    const html = new TextDecoder().decode(f.bytes);
    try {
      if (typeof ClipboardItem !== 'undefined' && f.mime.startsWith('text/html')) {
        const text = preview?.kind === 'text' ? preview.text : html.replace(/<[^>]+>/g, '');
        await navigator.clipboard.write([new ClipboardItem({ 'text/html': new Blob([html], { type: 'text/html' }), 'text/plain': new Blob([text], { type: 'text/plain' }) })]);
      } else {
        await navigator.clipboard.writeText(html);
      }
      setCopied(true);
    } catch (e) {
      setError(`Couldn’t copy: ${errorText(e)}`);
    }
  };
  const show = (fragmentId: string) => workspace?.reveal({ fragmentId });

  const checkpoint = built?.reports.length === 1 ? built.reports[0].checkpoint : null;

  return (
    <div className="publish-page" data-format={format?.id ?? ''} data-preset={presetId ?? ''}>
      <nav className="publish-formats" aria-label="Formats">
        <h1 tabIndex={-1} className="publish-heading">Publish</h1>
        {formatsError ? <p className="error" role="alert">{formatsError}</p> : null}
        {!formats && !formatsError ? <p className="status-note">Reading the presets…</p> : null}
        {formats ? (
          <>
            <FormatRows formats={formats.builtIn} selected={format?.id ?? null} onChoose={setFormatId} />
            {formats.more.length ? <p className="eyebrow publish-group">More formats</p> : null}
            <FormatRows formats={formats.more} selected={format?.id ?? null} onChoose={setFormatId} />
            {formats.yours.length || formats.problems.length ? <p className="eyebrow publish-group">Your formats</p> : null}
            <FormatRows formats={formats.yours} selected={format?.id ?? null} onChoose={setFormatId} />
            {formats.problems.map((p) => (
              <p className="publish-problem" key={p.id}>
                “{p.id}” has a mistake: {p.message}
              </p>
            ))}
          </>
        ) : null}
        {formats ? (
          <label className="publish-format-select">
            <span className="visually-hidden">Format</span>
            <select value={format?.id ?? ''} onChange={(e) => setFormatId(e.target.value)}>
              {all.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.title}
                </option>
              ))}
            </select>
          </label>
        ) : null}
      </nav>

      <section className="publish-stage" aria-label="Preview" aria-busy={!preview && !built}>
        {format?.kind === 'submission' ? (
          <div className="publish-chips" role="group" aria-label="Document">
            {format.variants.map((v) => (
              <button type="button" key={v.presetID} aria-pressed={v.presetID === presetId} onClick={() => setVariants({ ...variants, [format.id]: v.presetID })}>
                {v.label}
              </button>
            ))}
          </div>
        ) : null}
        <div className="publish-proof">
          {building && progress ? (
            <div className="publish-progress" role="status" data-pass={progress.pass}>
              <p>Setting the book: pass {progress.pass} of {progress.of}</p>
              <progress max={progress.of} value={progress.pass} />
              <p className="status-note">{plural(progress.pages, 'page')} so far. Scene breaks at a page edge and uneven facing pages are fixed as it’s set.</p>
            </div>
          ) : built?.layout && format?.kind !== 'submission' ? (
            <LaidOutPages pages={built.layout} book pageWidth={260} current={page} />
          ) : (
            <PreviewView preview={preview} onOpenPDF={openPDF} />
          )}
        </div>
        <p className="publish-caption">
          {built && built.pages !== null && format?.kind !== 'submission'
            ? `Built: ${built.files[0]?.name} · ${plural(built.pages, 'page')}`
            : built
              ? `Built: ${built.zip?.name ?? built.files[0]?.name}`
              : preview?.kind === 'tracks'
                ? 'Estimated from the text; the real reading may run a little longer or shorter'
                : preview && preview.kind !== 'message'
                  ? format?.kind === 'submission' || summary?.isDocument || summary?.isPost ? 'As it will be built' : 'A sample: the opening chapters, as they’ll be built'
                  : ''}
        </p>
      </section>

      <aside className="publish-details" aria-label="Details">
        {format ? (
          <>
            {adjusting && adjustable && presetId && opened.record ? (
              <FormatSettings
                project={opened}
                live={live}
                presetId={presetId}
                title={format.title}
                example={firstChapter}
                onDone={() => setAdjusting(false)}
                onSaved={(id) => {
                  if (id && !isAdjustedPreset(id) && !format.variants.some((v) => v.presetID === id)) setFormatId('preset:' + id);
                }}
              />
            ) : (<>
            <div className="publish-title">
              <h2>{format.title}</h2>
              <p>{format.lede}</p>
            </div>
            {format.variants.length > 1 && format.kind !== 'submission' ? (
              <label className="publish-row">
                <span>Version</span>
                <select value={presetId ?? ''} onChange={(e) => setVariants({ ...variants, [format.id]: e.target.value })}>
                  {format.variants.map((v) => (
                    <option key={v.presetID} value={v.presetID}>
                      {v.label}
                    </option>
                  ))}
                </select>
              </label>
            ) : null}
            {format.kind === 'post' ? (
              <label className="publish-row">
                <span>Post</span>
                {posts.length ? (
                  <select value={post ?? ''} onChange={(e) => setPostId(e.target.value)}>
                    {posts.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.label}
                      </option>
                    ))}
                  </select>
                ) : (
                  <em>None yet</em>
                )}
              </label>
            ) : null}
            {summary && format.kind !== 'submission' ? (
              <div className="publish-row publish-author">
                <span>Author</span>
                <span data-author>
                  {summary.blind ? 'Left out (blind)' : summary.publishedAs ? `${summary.publishedAs}${summary.penName ? ' (pen name)' : ''}` : 'Your name isn’t set'}
                  {!summary.blind ? <small>The pen name is chosen on your Mac or iPad (Publish ▸ Author).</small> : null}
                  {!summary.blind ? <BylineEditor project={project} live={live} owner={owner} ownerName={workspace?.ownerName ?? null} /> : null}
                </span>
              </div>
            ) : null}
            {preparation?.settings.length ? (
              <dl className="publish-settings">
                {preparation.settings.map(([k, v]) => (
                  <div key={k}>
                    <dt>{k}</dt>
                    <dd>{v}</dd>
                  </div>
                ))}
              </dl>
            ) : null}
            {adjustable && owner && format.kind !== 'submission' ? (
              <button type="button" className="link-button publish-adjust" onClick={() => { reset(); setAdjusting(true); }}>Adjust…</button>
            ) : null}
            {summary && summary.frontMatter && !summary.blind && !summary.audio ? (
              <div className="publish-publication">
                <button type="button" className="link-button" aria-expanded={showPublication} onClick={() => setShowPublication(!showPublication)}>
                  Publication details
                </button>
                {showPublication ? (
                  <div className="publish-note">
                    <dl className="publish-settings">
                      <div><dt>Publisher</dt><dd>{summary.publication.publisher || 'None'}</dd></div>
                      <div><dt>Copyright</dt><dd>{summary.publication.copyright || 'None'}</dd></div>
                      {summary.publication.edition ? <div><dt>ISBN</dt><dd>{summary.publication.isbn || 'None'}</dd></div> : null}
                    </dl>
                    <p>Publication details are edited on your Mac or iPad (Publish ▸ Publication details); they reach here with the project.</p>
                  </div>
                ) : null}
              </div>
            ) : null}
            {(format.kind === 'manuscript' || format.kind === 'screenplay') && summary && !summary.isDocument ? (
              <label className="publish-row publish-limit">
                <span>Word limit</span>
                <input inputMode="numeric" placeholder="None" value={limit} onChange={(e) => setLimit(e.target.value)} aria-describedby="limit-help" />
                <small id="limit-help">Preflight checks the build against a market’s limit.</small>
              </label>
            ) : null}

            <Checks
              built={built}
              preparation={preparation}
              error={error}
              notes={[
                ...(pdfMissing ? ['PDF isn’t in this build of the web app, so this format can’t be built here. Build it on your Mac or iPad.'] : []),
                ...(recordsAudio ? ['Audiobooks are recorded on your Mac or iPad. The narration script (Script, in Version) can be built here.'] : []),
                ...(live && !live.writable && owner ? ['The project is still opening: building records a checkpoint, which needs it open.'] : []),
              ]}
              copyfitMessage={copyfitMessage}
              onPage={built?.layout ? setPage : null}
              onShow={show}
              onTurnOn={owner && live?.writable ? turnOn : null}
            />

            <div className="publish-actions">
              {!owner ? (
                <p className="status-note owner-only" data-owner-only="publish">
                  Only {workspace?.ownerName || 'the project’s owner'} compiles this project.
                </p>
              ) : !built ? (
                <button type="button" className="pill" disabled={!canBuild} onClick={() => void build()} data-building={building ? 'true' : 'false'}>
                  {building ? 'Building…' : recordsAudio ? 'Record audiobook' : format.action}
                </button>
              ) : format.kind === 'post' ? (
                <>
                  <button type="button" className="pill" onClick={() => void copyPost()}>{copied ? 'Copied' : 'Copy for Substack'}</button>
                  <div className="publish-secondary">
                    <button type="button" className="pill outline" onClick={save}>Save…</button>
                    <button type="button" className="pill outline" onClick={() => void build()}>Build Again</button>
                  </div>
                </>
              ) : (
                <>
                  <button type="button" className="pill" onClick={save}>{format.kind === 'submission' ? 'Save the Pack…' : 'Save…'}</button>
                  <div className="publish-secondary">
                    {builtPDF ? <button type="button" className="pill outline" onClick={() => openPDF(builtPDF.bytes)} title="Opens the PDF in a new tab, to print from the browser’s viewer">Print…</button> : null}
                    {copyfit.length ? (
                      <button type="button" className="pill outline" disabled={!live?.writable} onClick={addCopyfit} title="Put each copyfitting suggestion in the margin as a comment on its paragraph. The text isn’t changed.">
                        Add Copyfitting Notes
                      </button>
                    ) : null}
                    <button type="button" className="pill outline" onClick={() => void build()}>Build Again</button>
                  </div>
                </>
              )}
              {filtered && owner ? (
                <label className="publish-only-filtered">
                  <input type="checkbox" checked={onlyFiltered} onChange={(e) => setOnlyFiltered(e.target.checked)} />
                  Only the filtered scenes ({filtered.documents} of {filtered.total})
                </label>
              ) : null}
              {checkpoint ? <p className="publish-checkpoint" data-checkpoint={checkpoint.id}>Recorded as checkpoint “{checkpoint.name}”</p> : null}
              {built && built.reports.length > 1 ? <p className="publish-checkpoint">Recorded {plural(built.reports.length, 'checkpoint')}, one for each document</p> : null}
            </div>
            </>)}
          </>
        ) : null}
      </aside>
    </div>
  );
}

function FormatRows({ formats, selected, onChoose }: { formats: PublishFormat[]; selected: string | null; onChoose: (id: string) => void }) {
  if (!formats.length) return null;
  return (
    <ul className="publish-format-list">
      {formats.map((f) => (
        <li key={f.id}>
          <button type="button" aria-current={f.id === selected ? 'true' : undefined} data-format-id={f.id} onClick={() => onChoose(f.id)}>
            <span className="publish-format-title">
              {f.title}
              {f.variants.some((v) => isAdjustedPreset(v.presetID)) ? <span className="publish-adjusted">Adjusted</span> : null}
            </span>
            <span className="publish-format-sub">{f.kind === 'audiobook' ? 'On your Mac or iPad' : f.subtitle}</span>
          </button>
        </li>
      ))}
    </ul>
  );
}

function PreviewView({ preview, onOpenPDF }: { preview: Preview | null; onOpenPDF: (bytes: Uint8Array) => void }) {
  if (!preview) return <p className="publish-waiting" role="status">Making a preview…</p>;
  switch (preview.kind) {
    case 'pdf':
      return (
        <div className="publish-pdf" data-preview="pdf">
          <LaidOutPages pages={preview.pages} book={preview.book} pageWidth={preview.book ? 260 : 340} />
          <button type="button" className="link-button" onClick={() => onOpenPDF(preview.pdf)}>
            Open the sample as a PDF
          </button>
        </div>
      );
    case 'html':
      return <SampleView html={preview.html} />;
    case 'text':
      return (
        <pre className="text-proof" data-preview="text">
          {preview.text}
        </pre>
      );
    case 'tracks':
      return (
        <div className="audiobook-estimate" data-preview="tracks">
          <div className="estimate-head">
            <div>
              <p className="estimate-title">{preview.estimate.sampleTitle}, sample</p>
              <p className="status-note">Recorded on your Mac or iPad, with the voice chosen there</p>
            </div>
            <p className="estimate-total">about {clock(preview.estimate.total, true)}</p>
          </div>
          <ol>
            {preview.estimate.tracks.map((t) => (
              <li key={t.id}>
                <span>{t.title}</span>
                <span>{clock(t.seconds, false)}</span>
              </li>
            ))}
          </ol>
        </div>
      );
    case 'message':
      return <p className="publish-message" data-preview="message">{preview.message}</p>;
  }
}

function SampleView({ html }: { html: string }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (ref.current) renderSample(ref.current, html);
  }, [html]);
  return <div className="ebook-sample" data-preview="html" ref={ref} />;
}

function Checks({ built, preparation, error, notes, copyfitMessage, onPage, onShow, onTurnOn }: {
  built: BuildResult | null;
  preparation: Preparation | null;
  error: string | null;
  notes: string[];
  copyfitMessage: string | null;
  onPage: ((page: number) => void) | null;
  onShow: (fragmentId: string) => void;
  /** Turns a readiness item's setting on (the owner, with the project open to write); null: no such action is offered. */
  onTurnOn: ((setting: FormatSetting) => void) | null;
}) {
  if (built) {
    const problems = built.findings.filter((f) => f.severity === 'error').length;
    return (
      <section className="publish-checks" aria-label="Preflight" data-checks="preflight">
        <p className="eyebrow">{problems === 0 ? 'Built, preflight passed' : `Built, ${plural(problems, 'problem')}`}</p>
        <ul>
          {built.findings.map((f, i) => (
            <FindingRow key={i} finding={f} onPage={onPage} onShow={onShow} />
          ))}
          {built.findings.length === 0 ? <CheckRow mark="✓" tone="ready" text="No problems found" /> : null}
        </ul>
        {copyfitMessage ? <p className="status-note" role="status" data-copyfit>{copyfitMessage}</p> : null}
        {error ? <p className="error" role="alert">{error}</p> : null}
      </section>
    );
  }
  return (
    <section className="publish-checks" aria-label="Before it’s built" data-checks="readiness">
      <p className="eyebrow">Before it’s built</p>
      <ul>
        {preparation?.error ? <CheckRow mark="!" tone="attention" text={preparation.error} /> : null}
        {preparation?.readiness.map((item: ReadinessItem) => (
          <CheckRow key={item.id} mark={item.status === 'ready' ? '✓' : item.status === 'attention' ? '!' : '·'} tone={item.status} text={item.message}
            onShow={item.fragmentID ? () => onShow(item.fragmentID!) : undefined}
            fix={item.setting && onTurnOn && FIX_TITLES[item.setting] ? { title: FIX_TITLES[item.setting]!, run: () => onTurnOn(item.setting!) } : undefined} />
        ))}
        {!preparation ? <li className="status-note">Checking…</li> : null}
        {notes.map((n) => <CheckRow key={n} mark="!" tone="attention" text={n} />)}
      </ul>
      {error ? <p className="error" role="alert">{error}</p> : null}
    </section>
  );
}

function FindingRow({ finding, onPage, onShow }: { finding: PreflightFinding; onPage: ((page: number) => void) | null; onShow: (fragmentId: string) => void }) {
  const info = finding.severity === 'info';
  return (
    <CheckRow
      mark={info ? '✓' : '!'}
      tone={info ? 'ready' : 'attention'}
      text={finding.message + (finding.copyfit ? ` ${finding.copyfit}` : '')}
      code={finding.code}
      onPage={finding.page && onPage ? () => onPage(finding.page!) : undefined}
      onShow={finding.fragmentID ? () => onShow(finding.fragmentID!) : undefined}
    />
  );
}

function CheckRow({ mark, tone, text, code, onPage, onShow, fix }: {
  mark: string; tone: string; text: string; code?: string; onPage?: () => void; onShow?: () => void;
  /** One action that answers the item ("Keep Them"), beside Show. */
  fix?: { title: string; run: () => void };
}) {
  return (
    <li className="check-row" data-tone={tone} data-code={code}>
      <span className="check-mark" aria-label={mark === '✓' ? 'Ready' : mark === '!' ? 'Check' : 'Note'}>{mark}</span>
      <span className="check-text">
        {text}
        {onPage || onShow || fix ? (
          <span className="check-links">
            {onPage ? <button type="button" className="link-button" onClick={onPage}>Page</button> : null}
            {onShow ? <button type="button" className="link-button" onClick={onShow}>Show</button> : null}
            {fix ? <button type="button" className="link-button" data-fix onClick={fix.run}>{fix.title}</button> : null}
          </span>
        ) : null}
      </span>
    </li>
  );
}
